import { BottomNavigation, BottomNavigationAction, Paper, useTheme } from '@mui/material';
import { useContext, useState, useEffect, useMemo } from 'react';
import { NavigationItem } from './NavigationItems';
import { Link } from 'react-router-dom';
import { AppContext } from '@/contexts/AppContext';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CalendarContext } from 'src/contexts/CalendarContext';
import { useActivePersona } from '@/personas/useActivePersona';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';

/**
 * Sant för den flik som är lägets "Idag" — den enda flik som återställer
 * kalendern till innevarande vecka när den trycks.
 *
 * Den finns på TVÅ länkar, en per läge: brukarens dagsvy ligger på '/' (vuxen,
 * barn och teen), medan handledarläget saknar '/'-flik — SuperviseGuard redirectar
 * '/' → '/supervisor'. Handledarens hem är alltså översikten, och det är den som
 * återställer veckan. Utan den andra jämförelsen tappar handledarläget
 * återställningen och veckovyn öppnas i den vecka handledaren senast bläddrade
 * till i stället för innevarande.
 *
 * Exporterad ren och testbar (ingen hook, ingen sidoeffekt).
 */
export const resetsToCurrentWeek = (link?: string): boolean =>
  link === '/' || link === '/supervisor';

const Navbar = () => {
  const location = useLocation();
  const theme = useTheme();
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);

  // Gaten sker i KOMPONENTEN (aldrig som modul-konstant) så att brukar-nav aldrig
  // läcker till vuxenläge och temabyte reagerar reaktivt. Nav-listan kommer nu
  // från den aktiva personan (child/teen/adult). isAdult ersätter det tidigare
  // !effectiveChildMode-villkoret: teen får 4-fliks-nav utan More-popup, som barn.
  const profile = useActivePersona();
  const items: NavigationItem[] = profile.navigation;

  const getCurrentIndex = () => {
    const index = items.findIndex((item) => item.link === location.pathname);
    return Math.max(index, 0);
  };
  const [value, setValue] = useState(getCurrentIndex());
  const { navbarRef, showNavbar, scrollNavbarVisible } = useContext(AppContext);
  // showNavbar = sidans egen (permanenta) vilja att visa baren alls, t.ex.
  // false under hela aktivitetsskapande-wizarden. scrollNavbarVisible = det
  // transienta scroll-baserade läget (dölj vid nedscroll, visa vid
  // uppscroll/scrollstopp). Baren ska bara synas när BÅDA tillåter det.
  const isNavbarVisible = showNavbar && scrollNavbarVisible;
  const { goToCurrentWeek } = useContext(CalendarContext);
  const { t } = useTranslation();

  useEffect(() => {
    setValue(getCurrentIndex());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, profile]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    // "Idag"-fliken återställer alltid till aktuell vecka (se resetsToCurrentWeek).
    if (resetsToCurrentWeek(items[newValue]?.link)) goToCurrentWeek();
    setValue(newValue);
  };

  const navigation: {
    items: JSX.Element[];
  } = useMemo(() => {
    return {
      items: items.map(({ link, text, icon }) => (
        // Ingen aria-label: den skrev över den synliga etiketten med sökvägen
        // ("/week"), så skärmläsaren sa något annat än det som står (2.5.3).
        <BottomNavigationAction
          label={t(text)}
          icon={icon}
          component={Link}
          to={link}
          key={link}
          sx={{ pb: '6px' }}
        />
      )),
    };
  }, [t, theme, items]);

  return (
    <Paper
      component="nav"
      aria-label={t('Navigation') as string}
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 99,
        transform: isNavbarVisible ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.25s ease-in-out',
        width: '100%',
        maxWidth: '100%',
        background: tk.card,
        borderTop: `1px solid ${tk.border}`,
      }}
      elevation={0}
      ref={navbarRef}
    >
      <BottomNavigation
        showLabels
        value={value}
        onChange={handleChange}
        sx={{
          justifyContent: 'center',
          height: 68,
          background: 'transparent',
          '& .MuiBottomNavigationAction-root': {
            color: tk.textSoft,
            borderRadius: '12px',
            mx: 0.5,
            transition: 'background 0.2s ease',
            '&.Mui-selected': {
              color: tk.primary,
              bgcolor: `${tk.primary}18`,
              '& .MuiBottomNavigationAction-label': {
                color: tk.primary,
                fontWeight: 700,
              },
            },
          },
        }}
      >
        {navigation.items}
      </BottomNavigation>
    </Paper>
  );
};

export default Navbar;
